/**
 * TopLocationsTable
 * Table showing top performing locations by energy/revenue
 */

import { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/core/components/ui/radio-group';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Skeleton } from '@/core/components/ui/skeleton';
import { TopLocation } from '../../types/dashboard-types';
import { useCurrencyFormatterRight } from '../../hooks/useCurrencyFormatter';

type SortBy = 'energy' | 'revenue' | 'sessions';

interface TopLocationsTableProps {
  locations: TopLocation[];
  isLoading?: boolean;
  onSortChange?: (sortBy: SortBy, limit: number) => void;
  defaultSortBy?: SortBy;
  defaultLimit?: number;
}

function getRankBadgeColor(rank: number): string {
  switch (rank) {
    case 1:
      return 'bg-yellow-100 text-yellow-800';
    case 2:
      return 'bg-gray-100 text-gray-800';
    case 3:
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-50 text-gray-600';
  }
}

export function TopLocationsTable({
  locations,
  isLoading = false,
  onSortChange,
  defaultSortBy = 'revenue',
  defaultLimit = 10
}: TopLocationsTableProps) {
  const formatCurrency = useCurrencyFormatterRight();
  const [sortBy, setSortBy] = useState<SortBy>(defaultSortBy);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultLimit);
  const isFirstRender = useRef(true);

  // Call onSortChange when sortBy or rowsPerPage changes (skip first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (onSortChange) {
      onSortChange(sortBy, rowsPerPage);
    }
  }, [sortBy, rowsPerPage, onSortChange]);

  // Show skeleton only on initial load, not when updating
  if (isLoading && !locations.length) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!locations.length) {
    return (
      <div className="h-[200px] flex items-center justify-center text-gray-500">
        No location data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls Row */}
      <div className="flex items-center justify-between">
        {/* Sort By Radio Buttons */}
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Sort by:</span>
          <RadioGroup
            value={sortBy}
            onValueChange={(value) => setSortBy(value as SortBy)}
            className="flex items-center gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="revenue" id="sort-revenue" />
              <Label htmlFor="sort-revenue" className="text-sm font-normal cursor-pointer">
                Revenue
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="energy" id="sort-energy" />
              <Label htmlFor="sort-energy" className="text-sm font-normal cursor-pointer">
                Energy
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sessions" id="sort-sessions" />
              <Label htmlFor="sort-sessions" className="text-sm font-normal cursor-pointer">
                Sessions
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Rows Per Page */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Rows per page:</span>
          <Select
            value={rowsPerPage.toString()}
            onValueChange={(value) => setRowsPerPage(Number(value))}
          >
            <SelectTrigger className="w-[80px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="30">30</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className={cn('border rounded-lg overflow-hidden', GRID_STYLES.border)}>
        <Table>
          <TableHeader className={GRID_STYLES.header.background}>
            <TableRow className={cn(GRID_STYLES.header.background, 'border-b', GRID_STYLES.border)}>
              <TableHead className={cn('w-16', GRID_STYLES.header.text)}>Rank</TableHead>
              <TableHead className={GRID_STYLES.header.text}>Location</TableHead>
              <TableHead className={cn('text-center', GRID_STYLES.header.text)}>Revenue</TableHead>
              <TableHead className={cn('text-center', GRID_STYLES.header.text)}>Energy (kWh)</TableHead>
              <TableHead className={cn('text-center', GRID_STYLES.header.text)}>Sessions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.slice(0, rowsPerPage).map((location) => (
              <TableRow key={location.locationId} className={GRID_STYLES.row.hover}>
                <TableCell>
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium ${getRankBadgeColor(
                      location.rank
                    )}`}
                  >
                    {location.rank}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">{location.locationName}</p>
                      <p className="text-sm text-gray-500">{location.city}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-center font-medium text-green-600">
                  {formatCurrency(location.revenueIls)}
                </TableCell>
                <TableCell className="text-center font-medium text-blue-600">
                  {location.energyKwh.toLocaleString()}
                </TableCell>
                <TableCell className="text-center font-medium text-purple-700">
                  {location.sessionsCount.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {isLoading && locations.length > 0 && (
        <div className="flex justify-center py-2">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
}
